<script>
  import { router, Link } from '@inertiajs/svelte'
  import ApplicationLayout from '../../components/ApplicationLayout/index.svelte'
  import EditIcon from '../../components/EditIcon/index.svelte'
  import StatusBadge from '../../components/StatusBadge.svelte'
  import DeleteConfirmation from '../../components/DeleteConfirmation.svelte'
  import StoredInSpine from '../../components/StoredInSpine/index.svelte'
  import ItemsList from '../../components/ItemsList.svelte'

  let { container, property = null, items = [], child_containers = [], can_manage_containers, pagination_meta = {} } = $props()
  let showDeleteConfirm = $state(false)
  let deleteItems = $state(false)
  let deleteChildren = $state(false)

  const propertyId = property?.id || container.property_id

  // The contained-items list (load more, controls, low-stock) is handled by the
  // shared ItemsList component, pointed at this container's items endpoint.

  function formatType(type) {
    if (!type) return 'Box'
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }

  function handleDeleteClick() {
    showDeleteConfirm = true
  }

  function handleDeleteConfirm({ checkboxChecked }) {
    router.delete(`/containers/${container.id}`, {
      data: {
        delete_items: checkboxChecked,
        delete_children: deleteChildren
      }
    })
  }

  function handleDeleteCancel() {
    showDeleteConfirm = false
    deleteItems = false
    deleteChildren = false
  }
</script>

<svelte:head>
  <title>{container.label} | Maset</title>
</svelte:head>

<ApplicationLayout>
  <!-- Breadcrumb -->
  <nav class="flex items-center gap-2 text-sm text-gray-500 mb-4 flex-wrap" aria-label="Breadcrumb">
    <Link href="/properties" class="hover:text-brand-700">Properties</Link>
    <span class="text-gray-400">/</span>
    {#if property?.name}
      <Link href={`/properties/${propertyId}`} class="hover:text-brand-700">{property.name}</Link>
    {:else}
      <Link href={`/properties/${propertyId}`} class="hover:text-brand-700">Property</Link>
    {/if}
    <span class="text-gray-400">/</span>
    <span class="font-semibold text-gray-900">{container.label}</span>
  </nav>

  <!-- Container hero -->
  <section class="relative rounded-2xl overflow-hidden bg-gradient-to-br from-brand-900 via-brand-700 to-teal-600 shadow-md">
    <div class="absolute inset-0 pointer-events-none" style="background: radial-gradient(600px 200px at 90% -20%, rgba(255,255,255,0.14), transparent 60%), radial-gradient(400px 180px at -10% 120%, rgba(255,255,255,0.08), transparent 60%);"></div>
    <div class="relative px-6 py-6 sm:px-8">
      <div class="flex flex-wrap justify-between items-start gap-4">
        <div class="flex items-start gap-4 min-w-0">
          {#if container.image_url}
            <img src={container.image_url} alt={container.label} class="h-16 w-16 flex-shrink-0 object-cover rounded-xl border border-white/30 shadow-sm" />
          {/if}
          <div class="min-w-0">
            <h1 class="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">{container.label}</h1>
            <div class="flex flex-wrap items-center gap-3 mt-2 text-sm text-white/85">
              <span class="bg-white/20 border border-white/25 px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider">
                {formatType(container.container_type)}
              </span>
              {#if container.space}
                <span class="inline-flex items-center gap-1.5">
                  <svg class="h-4 w-4 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  {container.space.name}
                </span>
              {/if}
            </div>
          </div>
        </div>
        {#if can_manage_containers}
          <div class="flex gap-2.5">
            <Link
              href={`/containers/${container.id}/edit`}
              class="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-brand-800 shadow-sm hover:bg-brand-50 transition-colors"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Edit
            </Link>
            <button
              onclick={handleDeleteClick}
              class="inline-flex items-center gap-2 rounded-lg bg-white/15 border border-white/30 px-4 py-2 text-sm font-semibold text-white hover:bg-white/25 transition-colors"
              title="Delete container"
              aria-label="Delete container"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          </div>
        {/if}
      </div>

      <!-- At-a-glance stats -->
      <div class="flex flex-wrap gap-2.5 mt-5">
        <div class="bg-white/10 border border-white/20 rounded-lg px-4 py-2 min-w-[6.5rem] flex-1 sm:flex-none">
          <b class="block text-xl font-extrabold text-white">{items.length}</b>
          <span class="text-[11px] uppercase tracking-wider text-white/75">Items</span>
        </div>
        <div class="bg-white/10 border border-white/20 rounded-lg px-4 py-2 min-w-[6.5rem] flex-1 sm:flex-none">
          <b class="block text-xl font-extrabold text-white">{child_containers.length}</b>
          <span class="text-[11px] uppercase tracking-wider text-white/75">Children</span>
        </div>
        <div class="bg-white/10 border border-white/20 rounded-lg px-4 py-2 flex-1 sm:flex-none flex items-center">
          <StatusBadge value={container.status} />
        </div>
      </div>
    </div>
  </section>

  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start mt-6">
    <!-- Main column -->
    <div class="lg:col-span-2 space-y-6">
      <!-- Stored in: where this container itself sits -->
      {#if container.stored_in && container.stored_in.length > 0}
        <section class="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100">
            <span class="w-7 h-7 rounded-lg grid place-items-center bg-brand-50 text-brand-700">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </span>
            <h2 class="text-base font-bold text-gray-900">Stored in</h2>
          </div>
          <div class="p-5">
            <StoredInSpine chain={container.stored_in} />
          </div>
        </section>
      {/if}

      <!-- Container images gallery -->
      {#if container.images && container.images.length > 1}
        <section class="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="px-5 py-4 border-b border-gray-100">
            <h2 class="text-base font-bold text-gray-900">Photos</h2>
          </div>
          <div class="p-5 grid grid-cols-3 sm:grid-cols-4 gap-2">
            {#each container.images as image (image.id)}
              <a href={image.url} target="_blank" rel="noopener" class="block aspect-square rounded-lg overflow-hidden border border-gray-200">
                <img src={image.thumbnail_url} alt={container.label} class="w-full h-full object-cover" />
              </a>
            {/each}
          </div>
        </section>
      {/if}

      <!-- Child containers -->
      {#if child_containers && child_containers.length > 0}
        <section class="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="flex justify-between items-center gap-3 px-5 py-4 border-b border-gray-100">
            <h2 class="text-base font-bold text-gray-900 flex items-center gap-2">
              Child Containers
              <span class="text-xs font-semibold text-gray-500 bg-gray-100 rounded-full px-2.5 py-0.5">{child_containers.length}</span>
            </h2>
            {#if can_manage_containers}
              <a href={`/properties/${propertyId}/containers/new?parent_container_id=${container.id}`} class="text-sm font-semibold text-brand-700 hover:underline">
                + Add child container
              </a>
            {/if}
          </div>

          <ul class="divide-y divide-gray-100 px-2 py-1.5">
            {#each child_containers as child}
              <li class="group flex items-center gap-3.5 px-3 py-2.5 rounded-xl hover:bg-gray-50">
                {#if child.image_url}
                  <img src={child.image_url} alt={child.label} class="h-13 w-13 flex-shrink-0 object-cover rounded-lg bg-gray-100" />
                {:else}
                  <div class="h-13 w-13 flex-shrink-0 rounded-lg bg-brand-50 text-brand-700 grid place-items-center">
                    <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M21 8l-9-5-9 5v8l9 5 9-5V8z"/><path d="M3 8l9 5 9-5"/></svg>
                  </div>
                {/if}
                <div class="flex-1 min-w-0">
                  <a href={`/containers/${child.id}`} class="text-sm font-semibold text-gray-900 hover:text-brand-700">
                    {child.label}
                  </a>
                  <div class="flex flex-wrap items-center gap-2 mt-0.5">
                    <span class="text-[11px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                      {formatType(child.container_type)}
                    </span>
                    <StatusBadge value={child.status} />
                    {#if child.is_valuable}
                      <svg class="h-3.5 w-3.5 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="Valuable"><title>Valuable</title><path d="M6 3h12l3 6-9 12L3 9l3-6z"/><path d="M3 9h18"/><path d="M9 3l3 6 3-6"/><path d="M12 9v12"/></svg>
                    {/if}
                    {#if child.is_favorite}
                      <svg class="h-3.5 w-3.5 text-red-500" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="Favorite"><title>Favorite</title><path d="M12 20s-7-4.5-9.5-9A5 5 0 0112 5a5 5 0 019.5 6c-2.5 4.5-9.5 9-9.5 9z"/></svg>
                    {/if}
                  </div>
                </div>
                {#if can_manage_containers}
                  <div class="flex items-center gap-1 flex-shrink-0 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity">
                    <EditIcon
                      href={`/containers/${child.id}/edit`}
                      label={`Edit ${child.label}`}
                      className="p-2 rounded-lg border border-gray-200 bg-white hover:text-brand-700 hover:border-brand-600"
                    />
                  </div>
                {/if}
              </li>
            {/each}
          </ul>
        </section>
      {/if}

      <!-- Items (shared component — same controls as the property page) -->
      <ItemsList
        items={items}
        propertyId={propertyId}
        paginationMeta={pagination_meta.items}
        loadUrl={`/containers/${container.id}/items`}
        addItemHref={`/properties/${propertyId}/items/new?container_id=${container.id}`}
      />
    </div>

    <!-- Sidebar -->
    <aside class="space-y-4">
      <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <h2 class="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Details</h2>
        <dl class="space-y-3 text-sm">
          <div class="flex justify-between gap-3">
            <dt class="text-gray-500">Type</dt>
            <dd class="font-semibold text-gray-900 text-right">{formatType(container.container_type)}</dd>
          </div>
          {#if container.dimensions}
            <div class="flex justify-between gap-3">
              <dt class="text-gray-500">Dimensions</dt>
              <dd class="font-semibold text-gray-900 text-right">{container.dimensions}</dd>
            </div>
          {/if}
          {#if container.color}
            <div class="flex justify-between gap-3">
              <dt class="text-gray-500">Color</dt>
              <dd class="font-semibold text-gray-900 text-right">{container.color}</dd>
            </div>
          {/if}
          {#if container.material}
            <div class="flex justify-between gap-3">
              <dt class="text-gray-500">Material</dt>
              <dd class="font-semibold text-gray-900 text-right">{container.material}</dd>
            </div>
          {/if}
          {#if container.purchase_date}
            <div class="flex justify-between gap-3">
              <dt class="text-gray-500">Purchased</dt>
              <dd class="font-semibold text-gray-900 text-right">{new Date(container.purchase_date).toLocaleDateString()}</dd>
            </div>
          {/if}
          {#if container.purchase_price}
            <div class="flex justify-between gap-3">
              <dt class="text-gray-500">Price</dt>
              <dd class="font-semibold text-gray-900 text-right">${container.purchase_price}</dd>
            </div>
          {/if}
        </dl>
        {#if container.notes}
          <p class="mt-4 text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2.5 whitespace-pre-line">{container.notes}</p>
        {/if}
      </div>
    </aside>
  </div>

  <DeleteConfirmation
    show={showDeleteConfirm}
    title="Delete Container"
    message="Are you sure you want to delete this container? This action cannot be undone."
    checkboxLabel="Delete all items in this container"
    bind:checkboxChecked={deleteItems}
    onConfirm={handleDeleteConfirm}
    onCancel={handleDeleteCancel}
  >
    {#snippet additionalOptions()}
    <div class="flex items-center">
      <label class="flex items-center">
        <input
          type="checkbox"
          bind:checked={deleteChildren}
          class="h-4 w-4 rounded border-gray-300 text-brand-700 focus:ring-brand-600"
        />
        <span class="ml-2 text-sm text-gray-700">Delete all child containers and their items</span>
      </label>
    </div>
    {/snippet}
  </DeleteConfirmation>
</ApplicationLayout>
